import { Fire, Hourglass, Trophy, Users } from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useState, type CSSProperties } from 'react';

import { useJoinFlashDrawMutation, type FlashDrawDto } from '@entities/flashDraw';
import { ProductLogo, useDealRemainingMs } from '@entities/product';
import { useGetMeQuery } from '@entities/user';
import { apiErrorCode, apiErrorMessage } from '@shared/api/baseApi';
import { haptics } from '@shared/lib/telegram';
import { useLogoAccent } from '@shared/lib/useLogoAccent';
import { InsufficientBalanceSheet } from '@shared/ui/InsufficientBalanceSheet';
import styles from './FlashDrawHero.module.css';

function clock(seconds: number): string {
  const safe = Math.max(0, seconds);
  const hours = Math.floor(safe / 3_600);
  const minutes = Math.floor((safe % 3_600) / 60);
  const remainder = safe % 60;
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

export function FlashDrawHero({ draw, clockOffsetMs }: { draw: FlashDrawDto; clockOffsetMs: number }) {
  const reducedMotion = useReducedMotion();
  const { data: user } = useGetMeQuery();
  const [join, { isLoading }] = useJoinFlashDrawMutation();
  const [error, setError] = useState<string | null>(null);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const accent = useLogoAccent(draw.product.logoUrl);
  const target = draw.status === 'UPCOMING'
    ? draw.startsAt
    : draw.status === 'LIVE'
      ? draw.endsAt
      : draw.drawAt ?? draw.endsAt;
  const remainingMs = useDealRemainingMs(target, clockOffsetMs);
  const seconds = Math.ceil(remainingMs / 1_000);
  const progress = Math.min(100, draw.participantCount / draw.participantLimit * 100);
  const live = draw.status === 'LIVE' && remainingMs > 0;

  const enter = async () => {
    setError(null);
    if (user && Number(user.balance.amount) < Number(draw.entryPrice.amount)) {
      haptics.notify('warning');
      setInsufficientBalance(true);
      return;
    }
    try {
      await join(draw.id).unwrap();
      haptics.notify('success');
    } catch (cause) {
      if (apiErrorCode(cause) === 'INSUFFICIENT_BALANCE') {
        haptics.notify('warning');
        setInsufficientBalance(true);
        return;
      }
      haptics.notify('error');
      setError(apiErrorMessage(cause, 'Could not join the Flash Draw.'));
    }
  };

  const state = draw.status === 'UPCOMING'
    ? { title: 'Upcoming', icon: <Hourglass size={15} weight="fill" />, detail: `${clock(seconds)} until opening` }
    : draw.status === 'LIVE'
      ? { title: 'Live', icon: <Fire size={15} weight="fill" />, detail: `${clock(seconds)} remaining` }
      : draw.status === 'FULL'
        ? { title: 'Full', icon: <Users size={15} weight="fill" />, detail: 'All slots filled' }
        : draw.status === 'DRAWING'
          ? { title: 'Drawing', icon: <Hourglass size={15} weight="fill" />, detail: `${clock(seconds)} until result` }
          : draw.status === 'COMPLETED'
            ? { title: 'Completed', icon: <Trophy size={15} weight="fill" />, detail: `Winner: ${draw.winnerName ?? 'Selected'}` }
            : { title: 'Cancelled', icon: <Hourglass size={15} weight="fill" />, detail: 'All entries refunded' };

  return (
    <>
      <motion.section
      className={styles.hero}
      style={{
        '--flash-accent': accent.color,
        '--flash-accent-ink': accent.ink,
      } as CSSProperties}
      initial={reducedMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      aria-labelledby="flash-draw-title"
    >
      <div className={styles.heading}>
        <span><Fire size={17} weight="fill" /> Flash Draw</span>
        {draw.status !== 'UPCOMING' ? (
          <strong className={styles.status}>{state.icon}{state.title}</strong>
        ) : null}
      </div>

      <div className={styles.productRow}>
        <ProductLogo src={draw.product.logoUrl} alt="" width={76} height={76} loading="eager" />
        <div>
          <h2 id="flash-draw-title">{draw.product.name}</h2>
          <strong className={styles.price}>{draw.entryPrice.label} <small>/ Entry</small></strong>
        </div>
      </div>

      <div className={styles.progressCopy}>
        <span><Users size={16} weight="fill" /><strong>{draw.participantCount}/{draw.participantLimit}</strong> Joined</span>
        <span>{state.detail}</span>
      </div>
      <div className={styles.progress} aria-hidden><span style={{ width: `${progress}%` }} /></div>

      {live ? (
        <button
          type="button"
          className={styles.join}
          disabled={isLoading || draw.userJoined}
          onClick={() => void enter()}
        >
          {draw.userJoined ? 'You joined this draw' : isLoading ? 'Joining…' : `Join for ${draw.entryPrice.label}`}
        </button>
      ) : null}
      {draw.status === 'CANCELLED' && draw.cancellationReason ? (
        <p className={styles.reason}>{draw.cancellationReason}</p>
      ) : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      </motion.section>
      <InsufficientBalanceSheet
        open={insufficientBalance}
        source="flash-draw"
        onClose={() => setInsufficientBalance(false)}
      />
    </>
  );
}
